import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import moment from "moment";

/**
 * Any date shape the app may receive. Firestore-backed reads yield real
 * Firestore Timestamps (`{ seconds, nanoseconds }`), while the REST API
 * serializes dates as ISO strings — the display helpers must handle both.
 */
type DateInput =
  | firebase.firestore.Timestamp
  | { seconds?: number; nanoseconds?: number; _seconds?: number; _nanoseconds?: number }
  | Date
  | string
  | number
  | null
  | undefined;

export class Timestamp extends firebase.firestore.Timestamp {
  static now = () => {
    return firebase.firestore.Timestamp.fromDate(new Date());
  };
  static fromMoment = (m: moment.Moment): firebase.firestore.Timestamp =>
    firebase.firestore.Timestamp.fromDate(m.toDate());

  static fromDate = (d: Date): firebase.firestore.Timestamp => {
    return firebase.firestore.Timestamp.fromDate(d);
  };

  /**
   * Normalize any supported date shape to a JS Date, or null when the value is
   * missing/unparseable. Supports Firestore Timestamps (instances or plain
   * `{ seconds, nanoseconds }` / `{ _seconds }` objects), ISO/RFC strings,
   * epoch numbers (seconds or milliseconds), and Date instances.
   */
  private static toJsDate = (value?: DateInput): Date | null => {
    if (value === undefined || value === null || value === "") return null;

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    // Firestore Timestamp instance (compat or modular) exposes toDate()
    if (typeof (value as any).toDate === "function") {
      const d = (value as any).toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    }

    // Plain object carrying Firestore-style seconds/nanoseconds
    const seconds = (value as any).seconds ?? (value as any)._seconds;
    if (typeof seconds === "number") {
      const nanos = (value as any).nanoseconds ?? (value as any)._nanoseconds ?? 0;
      const d = new Date(seconds * 1000 + Math.floor(nanos / 1e6));
      return isNaN(d.getTime()) ? null : d;
    }

    // Epoch number: values below 1e12 are treated as seconds, otherwise ms
    if (typeof value === "number") {
      const ms = value < 1e12 ? value * 1000 : value;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }

    // ISO / date string (fall back to Date for RFC-style strings)
    if (typeof value === "string") {
      const iso = moment(value, moment.ISO_8601, true);
      const d = iso.isValid() ? iso.toDate() : new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }

    return null;
  };

  static toMoment = (timestamp: DateInput): moment.Moment => {
    const d = Timestamp.toJsDate(timestamp);
    return d ? moment(d) : moment.invalid();
  };

  static toDisplayDate = (timestamp?: DateInput): string => {
    const d = Timestamp.toJsDate(timestamp);
    return d ? moment(d).format("DD.MM.YYYY") : "";
  };

  static toFormattedDate = (timestamp?: DateInput, format: string = "YYYY-MM-DD"): string => {
    const d = Timestamp.toJsDate(timestamp);
    return d ? moment(d).format(format) : "";
  };

  static ToDisplayTime = (timestamp?: DateInput): string => {
    const d = Timestamp.toJsDate(timestamp);
    return d ? moment(d).format("HH:mm") : "";
  };
}
